import { useEffect, useMemo, useState } from "react";

import { DeploymentService } from "@packages/client";

import { ObservabilityPipelineResourcesByService } from "Api/observability/pipeline/types";
import { Poller } from "src/organization/pages/project/environment/metrics/utils/polling/poller";
import PollerPool from "src/organization/pages/project/environment/metrics/utils/polling/pollerpool";
import { MetricsPollType } from "src/organization/pages/project/environment/metrics/utils/polling/type";
import useObservabilityPipelineHttpClient from "src/organization/pages/project/environment/observabilitySandbox/hooks/useObservabilityPipelineHttpClient";

import { buildMetrics } from "./util";

type Props = Omit<
  MetricsPollType<ObservabilityPipelineResourcesByService>,
  "timeframe" | "onChange"
> & {
  service: DeploymentService;
  enabled?: boolean;
  pollgroup: PollerPool;
  onChange?: (v?: ObservabilityPipelineResourcesByService) => void;
};

const fetchMetrics = (client, timeframe, service) =>
  client.getResourcesByService({
    timeframe,
    service
  });

const usePoll = ({
  onChange,
  onLoadingChange,
  service,
  enabled = true,
  pollgroup
}: Props) => {
  const [resource, setResource] =
    useState<ObservabilityPipelineResourcesByService>();
  const client = useObservabilityPipelineHttpClient();
  const { metrics, instances } = useMemo(() => {
    const data = buildMetrics(resource?.data);
    return data;
  }, [resource]);

  const poller = useMemo(
    () =>
      new Poller<ObservabilityPipelineResourcesByService>({
        onChange: resource => {
          onChange?.(resource);
          setResource(resource);
        },
        onError: () => onChange?.(),
        onLoadingChange
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service.name]
  );

  useEffect(() => {
    if (client) {
      poller.setAsyncFn(t => fetchMetrics(client, t, service.name));
      pollgroup.registerPollerIfNotExists(service.name, poller);
      pollgroup.setPollerPaused(service.name, !enabled);
    }
  }, [client, enabled, poller, pollgroup, service.name]);

  const servicesInstance = useMemo(
    () => (instances.length > 1 ? [...instances, "average"] : instances).sort(),
    [instances]
  );

  useEffect(() => {
    return () => pollgroup.unregisterPoller(service.name);
  }, [poller, pollgroup, service.name]);

  return { instances: servicesInstance, metrics, poller };
};

export default usePoll;
